import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, History, Loader2, RefreshCw } from 'lucide-react';

import {
    getCheckInHistory, getAppointmentEvents, getPartyHistory,
    type CheckInEventDto, type PartyHistoryRow,
} from '@/api/client';
/* ⚠️ `AppointmentEventDto` IS RE-EXPORTED BY `client` FOR SOME TYPES AND NOT THIS
   ONE — it lives in `api/types`. Importing it from the wrong module is a compile
   error rather than a silent `any`, which is why this line is explicit. */
import type { AppointmentEventDto } from '@/api/types';
import { SECTION_HEADER } from '@/lib/utils';
import { useRailSlotClose } from '@/lib/railSlot';
import { focusBasisLabel, focusScopeLabel, type RailFocus } from '@/lib/railFocus';
import { checkInEventLabel } from '@/lib/checkInEvents';

/**
 * ============================================================================
 * ⭐⭐ HISTORY — WHAT HAS HAPPENED TO THE THING YOU ARE POINTING AT
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"a new panel called History. Same concept as the others,
 * > when you hover a driver, it shows you the driver history. When you hover the
 * > appointment, it shows you the appointment history. Will also need an ALL
 * > button to reset it."*
 *
 * ---- ⛔⛔ IT IS TWO DIFFERENT TIMELINES, NOT ONE WITH A FILTER -------------
 *
 * ⚠️ POINTING AT THE MAN AND POINTING AT THE LOAD ASK DIFFERENT QUESTIONS, and
 * they are answered by different endpoints against different tables:
 *
 *   DRIVER      → GET mgmt/checkins/{id}/history   → dock.DriverCheckInEvent
 *   APPOINTMENT → GET appointments/{id}/events     → the appointment audit
 *
 * ⛔ SO `focus.kind` DECIDES, NOT WHICH IDS HAPPEN TO BE SET. A driver matched to
 * a load carries BOTH ids, and reading the appointment's timeline because it was
 * available would answer a question nobody asked — his licence scan and his gate
 * arrival are on the ARRIVAL, and they are the reason a scheduler hovers him.
 *
 * ---- ⚠️ "ALL" HAS NO TIMELINE OF ITS OWN, AND SAYS SO --------------------
 *
 * ⛔ THERE IS NO FACILITY-WIDE HISTORY ENDPOINT and this panel does not invent
 * one. Unscoped it renders one sentence telling the scheduler to hover something.
 * ⚠️ AN EMPTY LIST WOULD HAVE READ AS "nothing has happened today", which on a
 * panel whose whole job is the record is the worst available lie.
 *
 * ---- ⭐ WHY THIS PANEL EXISTS AT ALL --------------------------------------
 *
 * > Pete, immediately after: *"We can then remove Arrival History from the Driver
 * > Card, so it manages the space better."*
 *
 * ⛔ THE CARD'S COPY IS RETIRED IN THE SAME CHANGE. Shipping this without removing
 * that one would put the same eight lines on screen twice and cost the rail the
 * height the move was meant to reclaim. ⚠️ The `.slice(-8)` cap comes with it —
 * see the note on `LIMIT` below; it was there because the card had to stay short,
 * and this panel inherits the same rail.
 * ============================================================================
 */

/**
 * ---- ⛔⛤ ~~const LIMIT = 8~~ — STRUCK 2026-09-22 (§0.3) --------------------
 *
 * > Pete: *"No on the +8 Earlier. Show all the history in this screen. Scroll if
 * > necessary."*
 *
 * ⚠️ THE CAP WAS INHERITED, NOT CHOSEN FOR THIS PANEL. It came from the DRIVER
 * CARD (#289), where eight lines was what fitted inside a hover card that also
 * had to show a name, a time, a door and a phone number. This panel is a rail
 * section with its own scroll container, and it kept the card's constraint after
 * the card was retired.
 *   ** A LIMIT COPIED WITH THE CODE IS NOT A DECISION, IT IS A FOSSIL. THE
 *      REASON STAYED BEHIND IN THE COMPONENT IT CAME FROM. **
 *
 * ⛔⛔ AND THE "+ 8 earlier" LINE WAS THE TELL. It was added so the remainder was
 * never silently dropped — which was right — but a panel that must announce what
 * it is hiding is a panel that should not be hiding it. Pete could see the count
 * said 16 and the list showed 8.
 *   ** WHEN THE HONEST LABEL FOR A TRUNCATION IS LONGER THAN THE ROWS IT SAVED,
 *      THE TRUNCATION IS NOT PAYING FOR ITSELF. **
 *
 * ⭐ THE HEADER COUNT NOW MATCHES THE LIST, which it never did before. That is
 * worth more than the height: a number beside a list that disagrees with it
 * teaches people to distrust both.
 */

interface HistoryPanelProps {
    getToken: () => Promise<string>;
    /** ⭐ THE FRAME (2026-09-22), both halves. Required by the party branch; the
     *  appointment and driver branches are keyed on ids and do not need either —
     *  an appointment IS its own frame and a check-in is one visit. */
    facilityCode: string;
    /** `YYYY-MM-DD`, the day on the calendar. */
    date: string;
    focus?: RailFocus | null;
    onClearFocus?: () => void;
    /** Bumped by the rail's Refresh so this reloads with everything else. */
    refreshKey?: number;
}

/** ⭐ One shape for the list, whichever endpoint filled it. */
interface Line {
    whenUtc: string;
    label: string;
    actor: string | null;
}

function hhmm(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * ⚠️ AN UNRECOGNISED TYPE PRINTS ITS RAW CODE rather than vanishing — the same
 * rule `checkInEventLabel` follows. A renderer that silently drops what it does not
 * know turns a new event type into a gap in the record.
 *
 * ⛔⚤ ~~`e.notes?.trim() || e.eventType`~~ — FIXED 2026-09-20. This comment CITED
 * `checkInEventLabel` while implementing a weaker copy of it, so this rail printed
 * `CheckedIn` where the arrival card printed *"Checked in at the gate"*. Nobody
 * noticed while both were on screen; removing the card would have made it the only
 * rendering, and a downgrade nothing was watching for.
 *   ** A COMMENT THAT CITES ANOTHER IMPLEMENTATION IS A MISSING IMPORT. **
 *
 * ⚠️ `notes` STILL WINS. The server writes a specific sentence there for the cases
 * that have one (*"License already on file for this number"*), and the map is the
 * fallback for the rest — that order is unchanged.
 */
function checkInLine(e: CheckInEventDto): Line {
    const label = e.notes?.trim() || checkInEventLabel(e);
    return { whenUtc: e.createdUtc, label, actor: e.actorName };
}

/**
 * ---- ⭐⭐ A PARTY'S LINE CARRIES WHICH LOAD AND WHICH SITE (DECISION 215) ----
 *
 * ⛔⛔ THIS IS THE ONLY LIST IN THE RAIL THAT CROSSES BOTH. Every other history
 * is one load at one facility, so the load and the site are implied by what you
 * hovered. A client's list is `A00000367 · 200 · Rescheduled` from one site and
 * `A00000412 · 262 · Arrived` from another, interleaved by time — and a row that
 * says only "Rescheduled" is a row nobody can act on.
 *   ** WHEN A LIST STOPS BEING ABOUT ONE THING, EVERY ROW HAS TO SAY WHICH THING
 *      IT IS ABOUT. **
 *
 * ⚠️ THE SAME OPAQUE-ACTOR RULE AS `appointmentLine`, and for the same reason —
 * Pete, 2026-09-10: *"the history still has all those junk numbers in the
 * messages."* These rows come from `dock.AppointmentAudit` exactly as that one's
 * do, so an id without an `@` is dropped rather than shown.
 */
function partyLine(r: PartyHistoryRow): Line {
    const what = r.notes?.trim() || r.eventType;
    const where = [r.appointmentNumber?.trim() || null, r.facilityCode?.trim() || null]
        .filter(Boolean).join(' \u00b7 ');
    return {
        whenUtc: r.eventUtc,
        label: where ? `${where} \u00b7 ${what}` : what,
        actor: r.actorId && r.actorId.includes('@') ? r.actorId : null,
    };
}

function appointmentLine(e: AppointmentEventDto): Line {
    const label = e.notes?.trim() || e.eventType;
    /**
     * ⛔ THE ACTOR IS DROPPED WHEN IT IS AN OPAQUE ID — the same rule the server
     * now applies to arrival events after Pete, 2026-09-10: *"the history still
     * has all those junk numbers in the messages."* ⚠️ This audit is read through
     * a different route that has not been through that fix, so the test is
     * repeated here rather than assumed.
     */
    const actor = e.actorId && e.actorId.includes('@') ? e.actorId : null;
    return { whenUtc: e.eventUtc, label, actor };
}

export function HistoryPanel({
    /**
     * ⛔⤴ ~~`onClearFocus`~~ NO LONGER DESTRUCTURED (increment 3, 2026-09-19). The
     * per-panel `ALL` button is gone; ONE master reset at the top of the rail clears
     * the focus for all of them. ⚠️ The PROP stays in the interface so the page's
     * wiring is unchanged and so a panel can regain its own control without a
     * signature change — but nothing here may render one, or there would be five
     * controls for one action again.
     */
    /* ⛔⛤ ADDING A PROP TO THE INTERFACE IS HALF THE EDIT. `facilityCode` and
       `date` were declared above and never destructured here — two TS2304s at the
       one line that reads them (2026-09-22). The interface describes the contract;
       this line is what makes the values exist. */
    getToken, facilityCode, date, focus = null, refreshKey = 0,
}: HistoryPanelProps) {
    /** ⛔ Collapsed by default, like every rail section but the check-ins. */
    const [open, setOpen] = useState(false);
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('HISTORY', open, setOpen);

    const [lines, setLines] = useState<Line[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * ---- ⭐⭐ TWO FACTS THAT TRAVEL WITH A PARTY LIST (DECISION 215) ---------
     *
     * ⛔ `truncated` — the 200-row cap bit and there is no "load more". A list
     * that is cut and does not say so is exactly what the struck `+ N earlier`
     * line existed to prevent; the cap changed, the obligation did not.
     * ⛔⛔ `deniedScope` — `facilityCount === 0` means THE TENANCY BOUND RESOLVED
     * TO NOTHING and the query never ran. ⚠️ Rendering that as an empty history
     * would report a permissions problem as a fact about the client.
     *   ** AN EMPTY LIST IS ONLY HONEST IF IT SAYS WHY IT IS EMPTY. **
     * ⚠️ BOTH ARE CLEARED ON EVERY LOAD, including the non-party branches — a
     * `truncated` left over from the last client would label an appointment's
     * complete timeline as cut.
     */
    const [truncated, setTruncated] = useState(false);
    const [deniedScope, setDeniedScope] = useState(false);

    /**
     * ⛔⛔ A SEQUENCE NUMBER, BECAUSE THE SCOPE CHANGES FASTER THAN THE NETWORK.
     * Hovering three cards in a second starts three reads, and without this the
     * slowest one wins and the panel shows a driver the cursor has left. ⚠️ That
     * failure is invisible in testing and constant in use.
     */
    const seq = useRef(0);

    const load = useCallback(async () => {
        if (!focus) {
            setLines([]); setError(null);
            /* ⚠️ CLEARED HERE TOO. This early return skips the body, so a banner
               from the last party would survive a reset to ALL. */
            setTruncated(false); setDeniedScope(false);
            return;
        }
        const mine = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            let next: Line[];
            let cut = false;
            let denied = false;
            /**
             * ---- ⭐⭐⭐ DECISION 215 — THE HUB AND THE SUB-PICTURE (2026-09-22)
             *
             * > Pete: *"the appointment is the main item in this whole program,
             * > connecting the drivers, client, carrier, consignee and paperwork
             * > together. All the other ones besides appointment should show their
             * > individual comms, history, etc."*
             *
             * ⭐ THE BREADTH COMES FROM `focus.kind`, WHICH THE FOCUS ALREADY
             * CARRIES — so this needs no control, no icon and no menu, and
             * decision 194's removal of five per-panel buttons stands untouched.
             *
             * ⛔⛔ THE PARTY BRANCH HAD TO GO **ABOVE** THE `appointmentId` ONE,
             * AND THAT IS THE WHOLE EDIT. A client or carrier focus often carries
             * an `appointmentId` (the card it was clicked from), so the old order
             * caught it in the appointment branch and showed ONE LOAD's timeline
             * under a client's name — a sub-picture that looked like a complete
             * one and was neither.
             *   ** ORDERING A CHAIN OF `else if` BY WHICH FIELD HAPPENS TO BE SET
             *      IS ORDERING IT BY ACCIDENT. ORDER IT BY WHAT WAS ASKED FOR. **
             *
             * ⚠️ DRIVER IS DELIBERATELY UNCHANGED — Pete chose today's visit only.
             * Its `&& focus.checkInId` fallback into the appointment branch is
             * preserved exactly, including for a driver with no check-in.
             */
            if (focus.kind === 'driver' && focus.checkInId) {
                const r = await getCheckInHistory(token, focus.checkInId);
                next = (r.events ?? []).map(checkInLine);
            } else if (
                (focus.kind === 'client' || focus.kind === 'carrier' || focus.kind === 'consignee')
                && focus.partyCode
            ) {
                const out = await getPartyHistory(token, focus.kind, facilityCode, date, focus.partyCode);
                next = (out.rows ?? []).map(partyLine);
                cut = out.truncated === true;
                /* ⛔ `=== false`, NOT `!out.permitted`. An older server sends no
                   such field and `undefined` must mean "not reported", not
                   "denied" — a refusal banner on a working list is worse than no
                   banner at all. */
                denied = out.permitted === false;
            } else if (focus.appointmentId) {
                const events = await getAppointmentEvents(token, Number(focus.appointmentId));
                next = (events ?? []).map(appointmentLine);
            } else {
                next = [];
            }
            if (mine !== seq.current) return;
            setLines(next);
            setTruncated(cut);
            setDeniedScope(denied);
        } catch (err) {
            if (mine !== seq.current) return;
            /* ⚠️ NAMED, NOT SWALLOWED. An unreadable history is a different fact
               from an empty one and the panel must not render them alike. */
            setError(err instanceof Error ? err.message : String(err));
            setLines([]);
            setTruncated(false); setDeniedScope(false);
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    /**
     * ---- ⛔⛔ AND THE DEP ARRAY IS THE HALF THE COMPILER CANNOT SEE ----------
     *
     * ⚠️ `tsc` caught the missing destructure in thirty seconds. It would NOT have
     * caught a stale `[focus, getToken]`: that compiles, ships, and presents as
     * *"the History panel still shows yesterday's client loads after I change the
     * date"* — a bug somebody has to notice, reproduce and describe.
     *   ** A TYPE ERROR IS A MESSAGE. A STALE DEPENDENCY IS A SYMPTOM SOMEBODY
     *      ELSE HAS TO DESCRIBE TO YOU LATER. **
     *
     * ⭐ Both new values are read inside `load`, so both belong here. `date` matters
     * most: the party branch is keyed on the day, so without it the one thing this
     * decision is about would never refetch.
     * ⚠️ The appointment and driver branches read neither, and that is fine — a
     * dependency that is sometimes unused re-runs a fetch that returns the same
     * answer. The reverse is the panel lying.
     */
    }, [focus, getToken, facilityCode, date]);

    useEffect(() => { void load(); }, [load, refreshKey]);

    /* ⛔⛤ ~~lines.slice(-LIMIT)~~ / ~~const earlier~~ — struck with the cap above.
       ⚠️ `shown` IS KEPT AS A NAME rather than renaming every use to `lines`: the
       render below reads `shown.map`, and a rename touching both would be a
       bigger diff than the change it is carrying. The container it draws into is
       already `overflow-y-auto` (see the panel body), so "scroll if necessary"
       needed no new scroller — only the removal of the thing preventing it. */
    const shown = lines;

    return (
        <div className="flex min-h-0 flex-col border-t">
            <div className={`shrink-0 bg-card flex w-full items-center gap-2 px-3 py-2 ${SECTION_HEADER}`}>
                {/* ⛔ NO `flex-1` — the other three rails do not have it, and it was the
                    whole reason this header looked different. A title button that eats the
                    row pushes `(client)` from beside the panel name out to the right edge,
                    so the same label sat in two different places depending on which panel
                    you were looking at.
                    ** A CONTAINER THAT GROWS MOVES EVERY SIBLING, INCLUDING THE ONES
                       NOBODY MEANT TO MOVE. ** */}
                <button
                    type="button"
                    onClick={() => setOpen(v => !v)}
                    aria-expanded={open}
                    className="flex min-w-0 items-center gap-2 text-left"
                >
                    {open
                        ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    {/* ⭐ THE NAME WEARS THE ICON'S COLOUR (Pete, 2026-09-10) — amber
                        here, which is not in use as a panel identity elsewhere.
                        ⚠️ Amber DOES mean "warning" on rows inside these panels; it is
                        safe as an identity only because a panel NAME and a row BADGE are
                        never adjacent. Worth remembering if that ever stops being true. */}
                    <History className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <span className="text-sm font-medium text-amber-600">History</span>
                    {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </button>

                {/* ⛔ INCREMENT 3 — A PLAIN LABEL, NOT A BUTTON. The five per-panel `ALL`
                buttons are gone; one master reset at the top of the rail
                replaces them. ** A LABEL IS AN INDICATOR AND MUST READ IN
                EVERY STATE; A RESET IS A CONTROL AND MUST NOT BE ON SCREEN
                WHEN IT WOULD DO NOTHING. **
                ⚠️ Measured before the change: all four panels already share
                ONE focus, so every `ALL` already reset all four -- five
                controls for one action, and nothing said so. */}
                <span
                    title={focus ? `Showing only ${focusBasisLabel(focus)}` : 'Showing everything today'}
                    className="shrink-0 self-center text-[10px] leading-none text-muted-foreground"
                >
                    {focusScopeLabel(focus)}
                </span>
                {/* ⭐ THE FAR-RIGHT COUNT (Pete, 2026-09-20: *"History and WMS Orders
                    have no far right counts"*). ~~History is the one rail with no
                    count~~ — it has one now, in the same slot, same classes and same
                    position as Communications, Labor and Driver Check-ins.

                    ⛔⛔ IT IS SUPPRESSED WHEN THERE IS NO FOCUS, AND THAT IS NOT A
                    LAYOUT CHOICE. Unscoped, this panel renders a SENTENCE — "hover a
                    driver card or an appointment" — because there is no facility-wide
                    history endpoint. A `0` beside that sentence would assert that
                    nothing happened today, which is the one thing this panel must
                    never say by accident.
                    ** A COUNT OVER AN UNASKED QUESTION IS NOT ZERO, IT IS NOTHING.
                       THE TWO RENDER THE SAME AND MEAN OPPOSITE THINGS. **

                    ⚠️ It counts `lines`, not `shown`. The panel renders the last
                    LIMIT lines plus an "earlier" line accounting for the rest, so
                    `lines.length` is what the panel accounts for.
                    ** A COUNT IN A PANEL HEADER DESCRIBES WHAT IS IN THAT PANEL. ** */}
                {!loading && focus && (
                    <span
                        className="ml-auto shrink-0 self-center text-xs text-muted-foreground"
                        title={`History events for ${focusBasisLabel(focus)}`}
                    >
                        {lines.length}
                    </span>
                )}
                {/* ⛔ `ml-auto` REPLACES THE PUSH `flex-1` WAS PROVIDING, AND IT IS NOW
                    CONDITIONAL. The count span above carries the push whenever it is on
                    screen; while loading, or with nothing scoped, there is no count span
                    and the icon has to carry it itself or it slides in against the panel
                    name. Same swap Labor and Communications make.
                    ** REMOVING AN ELEMENT REMOVES WHATEVER LAYOUT IT WAS PROVIDING FOR ITS
                       NEIGHBOURS. THE HOLE IS NOT WHERE YOU DELETED. ** */}
                <button
                    type="button"
                    onClick={() => void load()}
                    title="Reload this history"
                    className={`shrink-0 self-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground ${loading || !focus ? 'ml-auto' : ''}`}
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                </button>
            </div>

            {open && (
                <div className="min-h-0 overflow-y-auto px-3 py-1.5">
                    {/**
                      * ⛔⛔ UNSCOPED IS A SENTENCE, NOT AN EMPTY LIST. There is no
                      * facility-wide history endpoint; rendering nothing here would say
                      * "nothing has happened", which is the one thing this panel must
                      * never say by accident.
                      */}
                    {!focus && (
                        <div className="rounded border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
                            Hover a driver card or an appointment to see its history.
                        </div>
                    )}

                    {/* ⛔⛤ ~~THE GREY SCOPE BOX~~ REMOVED 2026-09-19 (§0.3). Pete: *"I don't
                            like the gray box inside the panel and then the same small text line
                            underneath it saying the same thing."*
                            ⚠️ HE WAS DESCRIBING A REAL DUPLICATION, NOT A PREFERENCE: this panel
                            said the date THREE times — "Showing <date>" above, "an order outside
                            <date> would not be here" in the box, and "Nothing due <date>" below.
                            ** A BOX THAT RESTATES ITS NEIGHBOURS IS NOT CONTEXT, IT IS NOISE —
                               AND THE READER STOPS READING ALL THREE. **
                            ✅ The one thing only the box carried — the BLOCKED REASON — moves to a
                            plain line, because it is the sentence that stops a scheduler reading an
                            empty panel as "nothing was sent". ⛔ It must not be lost with the
                            chrome. */}

                    {error && (
                        <div className="mb-1.5 rounded border border-destructive/40 px-2 py-1.5 text-[11px] text-destructive">
                            Could not read the history — {error}
                        </div>
                    )}

                    {/**
                      * ---- ⛔⛔ A DENIED SCOPE IS NOT AN EMPTY HISTORY ------------
                      *
                      * `facilityCount === 0` means the tenancy bound resolved to no
                      * facilities and the query NEVER RAN. ⚠️ "Nothing recorded yet"
                      * would report a permissions problem as a fact about the client,
                      * and somebody would go and tell them so.
                      *   ** A REFUSAL RENDERED AS AN EMPTY RESULT IS A WRONG ANSWER
                      *      WITH A CONFIDENT FACE. **
                      * ⛔ IT IS CHECKED BEFORE THE EMPTY STATE, because both are true
                      * at once and only one of them is worth saying.
                      */}
                    {focus && !error && !loading && deniedScope ? (
                        <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                            This history could not be read — either this site is not visible
                            to your account, or the day could not be resolved. It is not a
                            statement about this party.
                        </div>
                    ) : focus && !error && !loading && lines.length === 0 && (
                        <div className="rounded border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
                            {/* ⭐ THE EMPTY STATE NAMES THE FRAME. "Nothing recorded yet"
                                beside a date picker invites the reading "this party has no
                                history", when the true answer is "none on this day". */}
                            {focus.kind === 'client' || focus.kind === 'carrier' || focus.kind === 'consignee'
                                ? 'No appointments for this party on the selected day.'
                                : 'Nothing recorded yet.'}
                        </div>
                    )}
                    {/* ⭐ THE CAP, SAID OUT LOUD. There is no "load more" behind this
                        list, so a cut that is not announced is a list quietly claiming
                        to be complete — which is the fault the struck `+ N earlier`
                        line was invented to avoid. The cap moved; the duty did not. */}
                    {truncated && !loading && (
                        <div className="mb-0.5 text-[10px] text-muted-foreground">
                            Showing the most recent {lines.length} — there are older events.
                        </div>
                    )}

                    {/* ⛔⛤ ~~{earlier > 0 && "+ N earlier"}~~ — struck 2026-09-22.
                        Nothing is withheld now, so there is no remainder to
                        account for. See the note on the struck LIMIT. */}

                    {/* Newest last, matching the appointment timeline and the card this
                        replaces, so the three read the same way. */}
                    {shown.map((l, i) => (
                        <div key={i} className="mt-0.5 flex gap-1.5 text-[11px] leading-tight">
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                                {l.whenUtc ? hhmm(l.whenUtc) : ''}
                            </span>
                            <span className="min-w-0">
                                <span className="font-medium">{l.label}</span>
                                {l.actor && <span className="text-muted-foreground"> · {l.actor}</span>}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
